import React, { useRef, useMemo, useContext, useState, useEffect, useCallback } from "react";
import { AgGridReact } from 'ag-grid-react';
import TextField from '@mui/material/TextField';
import '../ag-grid-theme-builder.css';
import IconButton from '@mui/material/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCartPlus } from '@fortawesome/free-solid-svg-icons'
import Swal from 'sweetalert2'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Box, CircularProgress, Typography, InputAdornment, Button, Chip, Link, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { CartContext } from '../context/CartContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../adapters/api';
import { ownUnitPrice } from '../utils/unitPrice';
import InsightTicker from '../components/insights/InsightTicker';
import { useInlineItems } from '../components/insights/useInsightInline';
import {
  normalizeThai,
  tokenize,
  maxDistanceFor,
  fuzzyScore,
  buildVocabulary,
  suggestCorrection,
} from '../utils/fuzzySearch';

// คอลัมน์ที่ซ่อนสำหรับแผนกขายหน้าร้านจนกว่าหัวหน้าจะปลดล็อค (server ก็ไม่ส่งข้อมูลนี้มาด้วย)
const LOCKED_PRICE_COLS = ['ต้นทุน', 'price_1', 'price_2', 'price_3', 'price_4'];

const matchesExactly = (tokens, { name, code }) =>
  tokens.every(token => name.includes(token) || code.includes(token));

const formatCountdown = (ms) => {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// ยอดสุทธิ = คงเหลือ + ค้างรับ − ค้างส่ง → สถานะสำหรับแสดงสีในเซลล์จำนวน
const NET_STATUS = {
  up:    { color: '#2e7d32', label: 'เข้าเพิ่ม' },
  down:  { color: '#ed6c02', label: 'ถูกจอง' },
  short: { color: '#d32f2f', label: 'ขาด' },
};

const getNetStock = (data) => {
  const stock = Number(data?.stock_qty) || 0;
  const inQty = Number(data?.in_qty) || 0;
  const outQty = Number(data?.out_qty) || 0;
  const net = stock + inQty - outQty;
  if (!inQty && !outQty) return { stock, inQty, outQty, net, status: null };
  const status = net <= 0 ? 'short' : net > stock ? 'up' : net < stock ? 'down' : null;
  return { stock, inQty, outQty, net, status };
};

const fmtQty = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });

const StockBreakdown = ({ stock, inQty, outQty, net }) => (
  <Box component="table" sx={{ fontSize: 13, borderCollapse: 'collapse', '& td': { px: 0.5 }, '& td:last-of-type': { textAlign: 'right' } }}>
    <tbody>
      <tr><td>คงเหลือ</td><td>{fmtQty(stock)}</td></tr>
      <tr><td>+ ค้างรับ</td><td>{fmtQty(inQty)}</td></tr>
      <tr><td>− ค้างส่ง</td><td>{fmtQty(outQty)}</td></tr>
      <tr style={{ borderTop: '1px solid rgba(255,255,255,0.5)', fontWeight: 700 }}>
        <td>= สุทธิ</td><td>{fmtQty(net)}</td>
      </tr>
    </tbody>
  </Box>
);

const ListProducts = () => {
  const { cartGuid, cart, addToCart, updateQtyItemInCart, priceLevel } = useContext(CartContext);
  const [unitOptions, setUnitOptions] = useState({});
  const gridApiRef = useRef(null);
  const prevDataRef = useRef(null);
  const updatedProductsRef = useRef([]);
  const cartRef = useRef(cart);
  const priceLevelRef = useRef(priceLevel);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState(""); // searchText หลัง debounce — ใช้กรอง grid
  const [screenType, setScreenType] = useState('pc'); // 'mobile' | 'tabletPortrait' | 'tabletLandscape' | 'pc'
  const isMobile = screenType === 'mobile';
  const hideCode = screenType !== 'pc';
  const hideExtraColumns = screenType === 'mobile' || screenType === 'tabletPortrait';
  // insight ฝังในหน้าจอ: สินค้าที่ยังมีของแต่คาดว่าจะหมดใน ≤ 7 วัน (null = ผู้ใช้ไม่ได้เปิดใช้)
  const coverBadges = useInlineItems();
  const [displayedCount, setDisplayedCount] = useState(0);
  const searchDebounceRef = useRef(null);

  // ดึงข้อมูลสินค้าด้วย React Query
  const {
    data: products = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await api.get("/api/products");
      return response.data;
    },
    refetchInterval: 30000,
  });

  // สถานะล็อคราคา (แผนกขายหน้าร้าน)
  const queryClient = useQueryClient();
  const { data: priceLock } = useQuery({
    queryKey: ["priceLock"],
    queryFn: async () => (await api.get("/api/priceLock")).data,
  });
  const restricted = !!priceLock?.restricted;
  const unlockedUntil = priceLock?.unlockedUntil || null;
  const [now, setNow] = useState(Date.now());
  const locked = restricted && !(unlockedUntil && unlockedUntil > now);
  // ไม่มีสิทธิ์ดูต้นทุน (backend ตัดค่าออกแล้ว) → ซ่อนคอลัมน์ · ซ่อนไว้ก่อนจนกว่าจะรู้สิทธิ์
  const costHidden = locked || !priceLock?.costVisible;

  const refreshPriceLock = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["priceLock"] });
    queryClient.invalidateQueries({ queryKey: ["products"] });
  }, [queryClient]);

  // นับถอยหลัง และล็อคกลับอัตโนมัติเมื่อหมดเวลา
  useEffect(() => {
    if (!restricted || !unlockedUntil) return;
    setNow(Date.now());
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const expire = setTimeout(refreshPriceLock, Math.max(0, unlockedUntil - Date.now()) + 500);
    return () => {
      clearInterval(tick);
      clearTimeout(expire);
    };
  }, [restricted, unlockedUntil, refreshPriceLock]);

  const handleUnlock = async () => {
    const result = await Swal.fire({
      title: "ปลดล็อคราคา",
      icon: "question",
      html:
        '<div>ให้หัวหน้ากรอกชื่อผู้ใช้และรหัสผ่านเพื่อแสดงราคาทั้งหมด 10 นาที</div>' +
        '<input id="unlock-username" class="swal2-input" placeholder="ชื่อผู้ใช้" autocomplete="off">' +
        '<input id="unlock-password" type="password" class="swal2-input" placeholder="รหัสผ่าน" autocomplete="new-password">',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "ปลดล็อค",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#1976d2",
      cancelButtonColor: "#757575",
      showLoaderOnConfirm: true,
      didOpen: () => document.getElementById("unlock-username")?.focus(),
      preConfirm: async () => {
        const username = document.getElementById("unlock-username").value.trim();
        const password = document.getElementById("unlock-password").value;
        if (!username || !password) {
          Swal.showValidationMessage("กรุณากรอกชื่อผู้ใช้และรหัสผ่าน");
          return false;
        }
        try {
          const res = await api.post("/api/priceLock/unlock", { username, password });
          return res.data;
        } catch (err) {
          Swal.showValidationMessage(err.response?.data?.message || "ปลดล็อคไม่สำเร็จ");
          return false;
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });
    if (!result.isConfirmed) return;
    refreshPriceLock();
    Swal.fire({
      title: "ปลดล็อคแล้ว",
      text: "แสดงราคาทั้งหมด 10 นาที",
      icon: "success",
      timer: 1200,
      showConfirmButton: false,
    });
  };

  const handleLock = async () => {
    try {
      await api.post("/api/priceLock/lock");
    } catch (err) {
      console.error("Error locking prices:", err);
    }
    refreshPriceLock();
  };

  const getRowId = useCallback(params => String(params.data.code), []);
  const defaultColDef = useMemo(() => ({ sortable: false }), []);

  const onGridReady = useCallback(params => {
    gridApiRef.current = params.api;
    if (updatedProductsRef.current.length) {
      params.api.applyTransaction({ add: updatedProductsRef.current });
      prevDataRef.current = updatedProductsRef.current;
    }
  }, []);

  const onGridCountChange = useCallback(() => {
    if (gridApiRef.current) setDisplayedCount(gridApiRef.current.getDisplayedRowCount());
  }, []);

  // ค้นแบบตรงก่อน ถ้าไม่เจอสักรายการ → ใช้แบบใกล้เคียง (รองรับพิมพ์ผิด/ตกหล่น เช่น "เหล็กล่อง")
  const productTexts = useMemo(
    () => products.map(p => ({
      name: normalizeThai(p.name_1),
      code: String(p.code ?? '').toLowerCase(),
    })),
    [products]
  );

  const searchState = useMemo(() => {
    const tokens = tokenize(appliedSearch);
    if (tokens.length === 0) return { tokens, fuzzy: false };
    const exactHit = productTexts.some(t => matchesExactly(tokens, t));
    return { tokens, fuzzy: !exactHit && tokens.some(tok => maxDistanceFor(tok) > 0) };
  }, [appliedSearch, productTexts]);

  // "คุณหมายถึง ..." — สร้าง vocabulary จากชื่อสินค้าเฉพาะตอนต้องใช้
  const vocabRef = useRef({ products: null, vocab: null });
  const suggestion = useMemo(() => {
    if (!searchState.fuzzy) return null;
    if (vocabRef.current.products !== products) {
      vocabRef.current = { products, vocab: buildVocabulary(products.map(p => p.name_1)) };
    }
    return suggestCorrection(appliedSearch, productTexts.map(t => `${t.name} ${t.code}`), vocabRef.current.vocab);
  }, [searchState, appliedSearch, products, productTexts]);

  useEffect(() => {
    if (gridApiRef.current) gridApiRef.current.onFilterChanged();
  }, [searchState]);

  // บอก AG Grid ว่ามี external filter
  const isExternalFilterPresent = useCallback(
    () => searchState.tokens.length > 0,
    [searchState]
  );

  // logic เช็คแต่ละแถว
  const doesExternalFilterPass = useCallback(node => {
    const data = node.data;
    const text = {
      name: normalizeThai(data.name_1),
      code: String(data.code ?? '').toLowerCase(),
    };
    if (!searchState.fuzzy) return matchesExactly(searchState.tokens, text);
    return fuzzyScore(searchState.tokens, `${text.name} ${text.code}`) !== Infinity;
  }, [searchState]);


  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      if (w <= 899) setScreenType('mobile');
      else if (w <= 1023) setScreenType('tabletPortrait');
      else if (w <= 1199) setScreenType('tabletLandscape');
      else setScreenType('pc');
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    if (!gridApiRef.current) return;
    gridApiRef.current.setColumnsVisible(['code'], !hideCode);
    gridApiRef.current.setColumnsVisible(
      ['สันกำแพง', 'สันทราย', 'ค้างรับ', 'ค้างส่ง'],
      !hideExtraColumns
    );
    gridApiRef.current.setColumnsVisible(LOCKED_PRICE_COLS, !locked);
    gridApiRef.current.setColumnsVisible(['ต้นทุน'], !costHidden);
  }, [screenType, locked, costHidden]);

  // ดึง unit options จาก API
  useEffect(() => {
    const fetchUnitOptions = async () => {
      try {
        const response = await api.get("/api/unitOptions");
        if (Array.isArray(response.data)) {
          // เมื่อ fetch unitOptions
          const unitData = response.data.reduce((acc, { ic_code, units, unit_base }) => {
            acc[ic_code] = { units, unit_base };
            return acc;
          }, {});
          setUnitOptions(unitData);
        } else {
          console.error("Invalid API response format");
        }
      } catch (error) {
        console.error("Error fetching unit options:", error);
      }
    };
    fetchUnitOptions();
  }, [locked]); // ราคาแต่ละหน่วยถูกตัดตอนล็อคราคา → ดึงใหม่เมื่อปลดล็อค/ล็อค

  const notifySuccess = (text) => 
    toast.success(text, {
      position: "bottom-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
  });

  // อัปเดต products ให้มี unit_options ก่อนส่งเข้า AgGrid
  const updatedProducts = useMemo(() => 
    products.map(product => {
      const unitData = unitOptions[product.code] || {}; // ดึงข้อมูลหน่วยสินค้า
      return {
        ...product,
        unit_options: unitData.units || [{ code: product.unit_standard, stand_value: 1, divide_value: 1 }],
        unit_base: unitData.unit_base || product.unit_standard, // ✅ ถ้าไม่มี unit_base ใช้หน่วยมาตรฐานแทน
      };
    }),
    [products, unitOptions]
  );
  updatedProductsRef.current = updatedProducts;
  cartRef.current = cart;
  priceLevelRef.current = priceLevel;

  // Update grid data via transactions to preserve scroll position, column widths, and filter state.
  // Initial mount: applyTransaction seeds the grid (no rowData prop is used).
  // Subsequent refreshes: diff old vs new and patch only changed rows via applyTransactionAsync.
  useEffect(() => {
    const api = gridApiRef.current;
    if (!api || !updatedProducts.length) return;

    const prev = prevDataRef.current;
    prevDataRef.current = updatedProducts;

    if (prev === null) {
      api.applyTransaction({ add: updatedProducts });
      return;
    }

    const prevMap = new Map(prev.map(p => [String(p.code), p]));
    const newMap  = new Map(updatedProducts.map(p => [String(p.code), p]));
    const add = [], update = [], remove = [];

    for (const [code, newRow] of newMap) {
      const prevRow = prevMap.get(code);
      if (!prevRow) add.push(newRow);
      else if (JSON.stringify(prevRow) !== JSON.stringify(newRow)) update.push(newRow);
    }
    for (const [code, prevRow] of prevMap) {
      if (!newMap.has(code)) remove.push(prevRow);
    }

    if (add.length || update.length || remove.length) {
      api.applyTransactionAsync({ add, update, remove });
    }
  }, [updatedProducts]);

  const formatNumber = (number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  };  

  // กำหนด column definitions ด้วย useMemo
  const colDefs = useMemo(
    () => [
      {
        colId: 'action',
        field: "code",
        headerName: "action",
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        pinned: "left",
        cellRenderer: (params) => {
          const onClick = async () => {
            const { code, name_1, unit_options = [], ยอดคงเหลือ } = params.data;
            let rawValue = null;
            for (let lvl = priceLevelRef.current; lvl >= 0; lvl--) {
              const key = `price_${lvl}`;
              const candidate = params.data[key];
              if (candidate !== null && candidate !== undefined && String(candidate).trim() !== "") {
                rawValue = candidate;
                break;
              }
            }
            const basePriceRaw = rawValue !== null ? rawValue : 0;
            const basePrice = parseFloat(String(basePriceRaw).replace(/,/g, "")) || 0;

            if (ยอดคงเหลือ === null) {
              const confirmation = await Swal.fire({
                title: "สินค้าหมดสต็อค",
                html: "กรุณายืนยันว่า <strong>คุณแจ้งจัดซื้อแล้ว</strong> ใช่หรือไม่?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#1976d2",
                cancelButtonColor: "#757575",
                confirmButtonText: "แจ้งแล้ว",
                cancelButtonText: "ยังไม่ได้แจ้ง",
              });
              if (!confirmation.isConfirmed) return;
            }
            let selectedUnit;
            if (unit_options.length === 1) {
              selectedUnit = unit_options[0].code;
            } else {
              const { value } = await Swal.fire({
                title: "เลือกหน่วยสินค้า",
                input: "select",
                confirmButtonColor: '#1976d2',
                inputOptions: unit_options.reduce((acc, unit) => {
                  // const basePrice = parseFloat(String(price_0).replace(/,/g, "")) || 0;
                  const stand     = parseFloat(String(unit.stand_value))  || 1;
                  const divide    = parseFloat(String(unit.divide_value)) || 1;
                  const ratioSV   = stand / divide;
                  const rawPrice  = ownUnitPrice(unit, priceLevelRef.current) ?? basePrice * ratioSV;
                  const unitPrice = Math.round(rawPrice * 100) / 100;
                  const priceText = formatNumber(unitPrice);
                  let desc = "";
                  if (unit.code !== params.data.unit_base) {
                    const fmtRatio = (n) => Number(n.toFixed(4)).toString();
                    desc = ratioSV >= 1
                      ? ` (1 ${unit.code} = ${fmtRatio(ratioSV)} ${params.data.unit_base})`
                      : ` (${fmtRatio(1 / ratioSV)} ${unit.code} = 1 ${params.data.unit_base})`;
                  }
                  acc[unit.code] = `${unit.code} = ${priceText} บาท${desc}`;
                  return acc;
                }, {}),
                inputPlaceholder: "เลือกหน่วย",
                showCancelButton: false,
              });    
              selectedUnit = value;                       
            }
            if (!selectedUnit) return;

            const existingItems = cartRef.current.filter(
              item =>
                String(item.item_code).trim() === String(params.data.code).trim() &&
                String(item.unit_code).trim() === String(selectedUnit).trim()
            );
            let selectedItem = null;
            if (existingItems.length > 0) {
              const { isConfirmed, dismiss } = await Swal.fire({
                title: "สินค้านี้มีอยู่ในตะกร้าแล้ว",
                text: "คุณต้องการเพิ่มจำนวนในรายการเดิม หรือสร้างรายการใหม่?",
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#1976d2",
                cancelButtonColor: "#757575",
                confirmButtonText: "เพิ่มในรายการเดิม",
                cancelButtonText: "สร้างรายการใหม่",
              });
              if (dismiss === Swal.DismissReason.backdrop) return;
              if (isConfirmed) {
                if (existingItems.length > 1) {
                  const { value: selectedIndex } = await Swal.fire({
                    title: "เลือกรายการที่ต้องการเพิ่ม",
                    input: "select",
                    inputOptions: existingItems.reduce((acc, item, index) => {
                      acc[index] = `#${index + 1} - ${item.item_name} = ${item.qty} ${item.unit_code}`;
                      return acc;
                    }, {}),
                    inputPlaceholder: "เลือกหนึ่งรายการ",
                    showCancelButton: true,
                    confirmButtonColor: '#1976d2',
                    cancelButtonColor: '#757575',
                  });
                  if (selectedIndex !== undefined) {
                    selectedItem = existingItems[selectedIndex];
                  } else {
                    return;
                  }
                } else {
                  selectedItem = existingItems[0];
                }
              }
            }
          
            // const basePrice    = parseFloat(String(price_0).replace(/,/g, "")) || 0;
            const unitObj = unit_options.find(u => u.code === selectedUnit) || {};
            const stand   = parseFloat(String(unitObj.stand_value))  || 1;
            const divide  = parseFloat(String(unitObj.divide_value)) || 1;
            const ratioSV = stand / divide;
            const rawPrice     = ownUnitPrice(unitObj, priceLevelRef.current) ?? basePrice * ratioSV;
            const finalPrice   = Math.round(rawPrice * 100) / 100;


            const netInfo = getNetStock(params.data);
            const priceLine = selectedItem
              ? `ราคา: ${formatNumber(finalPrice)} บาท/${selectedUnit}  (มีอยู่แล้ว ${selectedItem.qty} ${selectedUnit})`
              : `ราคา: ${formatNumber(finalPrice)} บาท/${selectedUnit}`;
            const stockLine = netInfo.status
              ? `<div style="margin-top:8px;font-size:14px;color:#555">
                   คงเหลือ ${fmtQty(netInfo.stock)} | ค้างรับ ${fmtQty(netInfo.inQty)} | ค้างส่ง ${fmtQty(netInfo.outQty)}
                   → <b style="color:${NET_STATUS[netInfo.status].color}">สุทธิ ${fmtQty(netInfo.net)} ${params.data.unit_standard}</b>
                 </div>`
              : '';

            const { value: quantity } = await Swal.fire({
              title: name_1,
              html: `<div>${priceLine}</div>${stockLine}`,
              input: "number",
              inputAttributes: {
                autocomplete: "off",
                style: "text-align: center; font-size: 30px;",
              },
              inputValidator: (value) => {
                const n = parseInt(value, 10);
                if (!value || isNaN(n)) return "กรุณาใส่จำนวน";
                if (n <= 0) return "จำนวนต้องมากกว่า 0";
                return null;
              },
              showCancelButton: false,
              confirmButtonColor: '#1976d2',
              inputAutoFocus: true,
            });
            if (!quantity) return;

            // เตือนเมื่อสั่งเกินยอดสุทธิ (เทียบในหน่วยมาตรฐานที่ใช้แสดงสต็อค)
            // ข้ามกรณีสต็อคหมดและไม่มีค้างรับ/ค้างส่ง เพราะยืนยันแจ้งจัดซื้อไปแล้วด้านบน
            const toStdRatio = selectedUnit === params.data.unit_standard
              ? 1
              : params.data.unit_base === params.data.unit_standard ? ratioSV : null;
            const skipNetWarn = ยอดคงเหลือ === null && !netInfo.status;
            if (toStdRatio !== null && !skipNetWarn) {
              const requestedStd = parseInt(quantity, 10) * toStdRatio;
              if (requestedStd > netInfo.net) {
                const { isConfirmed } = await Swal.fire({
                  title: "จำนวนเกินยอดสุทธิ",
                  html: `ต้องการ ${fmtQty(requestedStd)} ${params.data.unit_standard}
                         แต่ยอดสุทธิมี <b>${fmtQty(netInfo.net)}</b> ${params.data.unit_standard}<br/>
                         (คงเหลือ ${fmtQty(netInfo.stock)} + ค้างรับ ${fmtQty(netInfo.inQty)} − ค้างส่ง ${fmtQty(netInfo.outQty)})<br/><br/>
                         ต้องการเพิ่มลงตะกร้าต่อหรือไม่?`,
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonColor: "#1976d2",
                  cancelButtonColor: "#757575",
                  confirmButtonText: "ยืนยันเพิ่ม",
                  cancelButtonText: "ยกเลิก",
                });
                if (!isConfirmed) return;
              }
            }

            try {
              if (selectedItem) {
                // เพิ่มจำนวนในรายการเดิม (ราคาไม่เปลี่ยน)
                await updateQtyItemInCart({
                  cartGuid,
                  roworder: selectedItem.roworder,
                  quantity: parseInt(quantity, 10),
                });
              } else {
                // สร้างรายการใหม่ ส่งราคาต่อหน่วย (base unit) เป็น finalPrice
                await addToCart({
                  code,
                  name: name_1,
                  quantity: parseInt(quantity, 10),
                  unit: selectedUnit,
                  unitPrice: finalPrice,
                });
              }
              notifySuccess(`${name_1} = ${quantity} ${selectedUnit}`);
            } catch (err) {
              Swal.fire({
                title: "เกิดข้อผิดพลาด",
                text: "ไม่สามารถเพิ่มสินค้าลงตะกร้าได้",
                icon: "error",
                confirmButtonColor: "#1976d2",
                confirmButtonText: "ตกลง",
              });
            }
                    
          };

          return (
            <IconButton color="primary" onClick={onClick}>
              <FontAwesomeIcon icon={faCartPlus} size="sm" />
            </IconButton>
          );
        },
      },
      {
        field: "name_1",
        headerName: "รายการสินค้า",
        cellClass: 'large-text',
        flex: 40,
        minWidth: 220,
        // ใช้ cellRendererFramework สำหรับ React component
        cellRenderer: (params) => {
          const { price_currency, item_pattern, is_new, new_arrival, is_recent, name_1 } = params.data;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              {item_pattern === 'AA' && (
                <i
                  className="fa-solid fa-star"
                  style={{ color: '#f5c542', marginRight: 4, fontSize: 14 }}
                />
              )}  
              {is_recent === 1 && price_currency === 1 && (
                <i
                  className="fa-solid fa-arrow-up fa-bounce"
                  style={{ color: '#ff0000', marginRight: 4, fontSize: 20 }}
                />
              )}
              {is_recent === 1 && price_currency === 2 && (
                <i
                  className="fa-solid fa-arrow-down fa-bounce"
                  style={{ color: '#008000', marginRight: 4, fontSize: 20 }}
                />
              )}
              {is_new === 1 && (
               <span style={{ color: '#FF4500', marginRight: 4, fontSize: 14, fontWeight: 'bold' }}>NEW! </span>
              )}
              {new_arrival === 1 && (
                <span style={{ color: '#6F42C1', marginRight: 4, fontSize: 14, fontWeight: 'bold' }}>RESTOCK! </span>
               )}
              

              {/* ชื่อสินค้า */}
              <span>{name_1}</span>
              {coverBadges?.[params.data.code] && (() => {
                const c = coverBadges[params.data.code];
                const days = Math.max(1, Math.round(c.daysLeft));
                // ยิ่งใกล้หมดยิ่งเข้ม: ≤2 วัน แดง · ≤4 วัน ส้ม · ที่เหลือ เหลืองอำพัน
                const tone = days <= 2
                  ? { fg: '#c62828', bg: '#ffebee', bd: '#ef9a9a' }
                  : days <= 4
                    ? { fg: '#e65100', bg: '#fff3e0', bd: '#ffcc80' }
                    : { fg: '#8d6e00', bg: '#fff8e1', bd: '#ffe082' };
                const fmt = n => n.toLocaleString('th-TH');
                return (
                  <Tooltip
                    arrow enterTouchDelay={0}
                    title={
                      <Box sx={{ py: 0.25, fontSize: 12, lineHeight: 1.6 }}>
                        <Box sx={{ fontWeight: 700, mb: 0.25 }}>คาดว่าของจะหมดในอีกประมาณ {days} วันทำการ</Box>
                        <Box>ยอดสุทธิ: {fmt(c.net)} {c.unit}</Box>
                        <Box sx={{ opacity: 0.8, pl: 1 }}>
                          = คงเหลือ {fmt(c.balance ?? 0)}{c.accruedIn > 0 ? ` + ค้างรับ ${fmt(c.accruedIn)}` : ""}{c.accruedOut > 0 ? ` − ค้างส่ง ${fmt(c.accruedOut)}` : ""}
                        </Box>
                        <Box>ขายเฉลี่ย: วันละ {fmt(c.perDay)} {c.unit} (28 วันล่าสุด)</Box>
                      </Box>
                    }
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 0.75, px: 0.9, py: '1px',
                        fontSize: 11.5, fontWeight: 600, lineHeight: 1.5, whiteSpace: 'nowrap', verticalAlign: 'middle',
                        color: tone.fg, bgcolor: tone.bg, border: `1px solid ${tone.bd}`, borderRadius: 999, cursor: 'help',
                      }}
                    >
                      <i className="fa-solid fa-hourglass-half" style={{ fontSize: 10 }} />
                      หมดใน ~{days} วัน
                    </Box>
                  </Tooltip>
                );
              })()}
            </span>
          );
        },
      },
      { field: "code", headerName: "รหัส", flex: 9, hide: hideCode },
      { field: "unit_standard", headerName: "หน่วย", flex: 7, cellClass: 'cell-center large-text' },
      {
        field: "ยอดคงเหลือ",
        headerName: "จำนวน",
        headerTooltip: "ตัวเลขเล็กด้านขวา = ยอดสุทธิ (คงเหลือ + ค้างรับ − ค้างส่ง)",
        flex: 10,
        minWidth: 110,
        cellClass: 'cell-center large-text bold-text',
        cellRenderer: (params) => {
          const isOut = params.value === null || params.value === undefined || params.value === '';
          const main = isOut
            ? <span style={{ color: '#d32f2f' }}>หมด</span>
            : <span style={{ color: '#1565c0' }}>{params.value}</span>;
          const netInfo = getNetStock(params.data);
          if (!netInfo.status) return main;
          const { color } = NET_STATUS[netInfo.status];
          const badge = netInfo.status === 'short'
            ? (netInfo.net < 0 ? `ขาด ${fmtQty(-netInfo.net)}` : '▼0')
            : `${netInfo.status === 'up' ? '▲' : '▼'}${fmtQty(netInfo.net)}`;
          return (
            <Tooltip title={<StockBreakdown {...netInfo} />} arrow enterTouchDelay={0} leaveTouchDelay={4000}>
              <span style={{ cursor: 'help' }}>
                {main}
                <span style={{ color, fontSize: '0.75em', marginLeft: 4 }}>{badge}</span>
              </span>
            </Tooltip>
          );
        },
      },
      { field: "สันกำแพง", headerName: "สันกำแพง", flex: 8, cellClass: 'cell-center text-blue', hide: hideExtraColumns },
      { field: "สันทราย", headerName: "สันทราย", flex: 8, cellClass: 'cell-center text-blue', hide: hideExtraColumns },
      { field: "ต้นทุน", headerName: "ต้นทุน", flex: 7, cellClass: 'cell-center bg-cost large-text', hide: costHidden },
      { field: "price_0", headerName: "สด", flex: 7, cellClass: 'cell-center bg-price-0 large-text' },
      { field: "price_1", headerName: "7%", flex: 7, cellClass: 'cell-center bg-price-1 large-text', hide: locked },
      { field: "price_2", headerName: "10%", flex: 7, cellClass: 'cell-center bg-price-2 large-text', hide: locked },
      { field: "price_3", headerName: "12%", flex: 7, cellClass: 'cell-center bg-price-3 large-text', hide: locked },
      { field: "price_4", headerName: "15%", flex: 7, cellClass: 'cell-center bg-price-4 large-text', hide: locked },
      { field: "ค้างรับ", headerName: "ค้างรับ", flex: 7, cellClass: 'cell-center', hide: hideExtraColumns },
      { field: "ค้างส่ง", headerName: "ค้างส่ง", flex: 7, cellClass: 'cell-center', hide: hideExtraColumns },
    ],
    [screenType, locked, costHidden, coverBadges]
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0, 
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default", 
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
          กำลังโหลดข้อมูลสินค้า…
        </Typography>
      </Box>
    );
  }
  if (error) return <div>Error loading products</div>;

    return (
    <>
      {/* 100dvh = ความสูงจอที่เห็นจริงบนมือถือ (ไม่รวมแถบที่อยู่เบราว์เซอร์) — เบราว์เซอร์เก่าใช้ 100vh */}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--nav-h))', '@supports (height: 100dvh)': { height: 'calc(100dvh - var(--nav-h))' } }}>
        {/* Search bar */}
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          px: { xs: 1, sm: 1.5 },
          pt: { xs: 1, sm: 1.5 },
          pb: 1,
          flexShrink: 0,
        }}>
          <TextField
            placeholder="ค้นหาสินค้า รหัส หรือชื่อ..."
            variant="outlined"
            size="small"
            value={searchText}
            onChange={e => {
              const value = e.target.value;
              setSearchText(value);
              clearTimeout(searchDebounceRef.current);
              searchDebounceRef.current = setTimeout(() => setAppliedSearch(value), 300);
            }}
            sx={{ flex: '1 1 260px', maxWidth: { sm: 600 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText('');
                      setAppliedSearch('');
                      clearTimeout(searchDebounceRef.current);
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {!isLoading && (
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {displayedCount.toLocaleString()} รายการ
            </Typography>
          )}
          <Tooltip title="ตัวเลขเล็กในช่องจำนวน = คงเหลือ + ค้างรับ − ค้างส่ง (แตะที่ตัวเลขเพื่อดูรายละเอียด)" arrow enterTouchDelay={0}>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, flexShrink: 0, cursor: 'help' }}>
              <Typography variant="caption" color="text.secondary">ยอดสุทธิ:</Typography>
              {Object.entries(NET_STATUS).map(([key, { color, label }]) => (
                <Typography key={key} variant="caption" sx={{ color, whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {key === 'up' ? '▲' : key === 'down' ? '▼' : '●'} {label}
                </Typography>
              ))}
            </Box>
          </Tooltip>
          {restricted && (locked ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={handleUnlock}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              ปลดล็อคราคา
            </Button>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Chip
                size="small"
                color="success"
                icon={<LockOpenIcon />}
                label={`แสดงราคา ${formatCountdown(unlockedUntil - now)}`}
              />
              <Button size="small" variant="text" color="inherit" startIcon={<LockIcon />} onClick={handleLock}>
                ล็อค
              </Button>
            </Box>
          ))}
          {/* KPI + insight รายวันทีละเรื่อง: จอคอม = ชิดขวาของแถวค้นหา · มือถือ/แท็บเล็ต (< lg) = บรรทัดบนสุดเหนือช่องค้นหา เต็มความกว้าง */}
          <Box sx={{ order: { xs: -1, lg: 0 }, flex: { xs: '1 1 100%', lg: '1 1 380px' }, maxWidth: { lg: 560 }, ml: { lg: 'auto' }, minWidth: 0, display: 'flex', '&:empty': { display: 'none' } }}>
            <InsightTicker />
          </Box>
        </Box>

        {searchState.fuzzy && (
          <Box sx={{ px: { xs: 1, sm: 1.5 }, pb: 1, flexShrink: 0 }}>
            <Typography variant="body2" color="text.secondary">
              ไม่พบ "{appliedSearch.trim()}" — แสดงผลลัพธ์ใกล้เคียง
              {suggestion && (
                <>
                  {' · คุณหมายถึง '}
                  <Link
                    component="button"
                    variant="body2"
                    fontWeight={600}
                    onClick={() => {
                      clearTimeout(searchDebounceRef.current);
                      setSearchText(suggestion);
                      setAppliedSearch(suggestion);
                    }}
                    sx={{ verticalAlign: 'baseline' }}
                  >
                    {suggestion}
                  </Link>
                  {' ?'}
                </>
              )}
            </Typography>
          </Box>
        )}

        {/* Grid — fills remaining height, scrolls horizontally on small screens */}
        <Box sx={{ flex: 1, minHeight: 0, overflowX: 'auto' }}>
          <div className="ag-theme-custom" style={{ height: '100%', minWidth: isMobile ? 720 : 900 }}>
            <AgGridReact
              onGridReady={onGridReady}
              getRowId={getRowId}
              columnDefs={colDefs}
              domLayout="normal"
              defaultColDef={defaultColDef}
              suppressMovableColumns={true}
              isExternalFilterPresent={isExternalFilterPresent}
              doesExternalFilterPass={doesExternalFilterPass}
              enableCellTextSelection={true}
              rowBuffer={20}
              onFirstDataRendered={onGridCountChange}
              onFilterChanged={onGridCountChange}
              onModelUpdated={onGridCountChange}
            />
          </div>
        </Box>
      </Box>

      <ToastContainer
        position="bottom-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 9999 }}
      />

    </>
  );
};

export default ListProducts;
